import { useQuery } from '@tanstack/react-query';
import {
  MatchRoomMetadata,
  MatchHistoryEntry,
  VerifySeedResponse,
  MatchStatus,
  Currency,
} from '@solante/shared';
import { amountToLamports } from '../utils/format';
import { http } from './http';

export interface SerializedInstruction {
  programId: string;
  keys: Array<{ pubkey: string; isSigner: boolean; isWritable: boolean }>;
  data: string;
}

interface CreateMatchInput {
  currency: 'SOL' | 'USDC';
  amount: string;
  serverSeed: string;
  cluster: string;
  creatorWallet: string;
}

export interface CreateMatchIntentResponse {
  matchId: string;
  instructions: SerializedInstruction[];
  matchAccount: string;
  escrowAccount: string;
  nonce: number;
  serverSeedHash: string;
}

export const requestCreateMatch = async ({
  currency,
  amount,
  serverSeed,
  cluster,
  creatorWallet,
}: CreateMatchInput): Promise<CreateMatchIntentResponse> => {
  const lamports = amountToLamports(Number(amount), currency);
  const payload = {
    wager: {
      currency,
      amountLamports: lamports.toString(),
    },
    serverSeed,
    cluster,
    creatorWallet,
  };
  return http.post<CreateMatchIntentResponse>('/matches', payload);
};

export const confirmMatch = async (matchId: string, signature: string) =>
  http.post<{ matchId: string; roomId: string; serverSeedHash: string }>(
    `/matches/${matchId}/confirm`,
    { signature },
  );

export const requestJoinIntent = async (matchId: string, wallet: string) =>
  http.post<{ instructions: SerializedInstruction[] }>(`/matches/${matchId}/join-intent`, {
    wallet,
  });

export const confirmJoin = async (matchId: string, wallet: string, signature: string) =>
  http.post<{ matchId: string; status: MatchStatus; players: string[] }>(
    `/matches/${matchId}/join/confirm`,
    { wallet, signature },
  );

export const useOpenMatches = () =>
  useQuery({
    queryKey: ['matches', 'open'],
    queryFn: () => http.get<{ matches: MatchRoomMetadata[] }>('/matches/open'),
    select: (data) => data.matches,
    refetchInterval: 5_000, // Poll every 5s for lobby updates
    refetchOnMount: 'always', // Always fetch fresh data on mount
    staleTime: 2_000, // Consider data stale after 2s
    placeholderData: (previousData) => previousData, // Keep previous data during refetch for smooth transitions
  });

export const fetchActiveMatches = async (wallet: string): Promise<MatchDetails[]> => {
  const response = await http.get<{ matches: MatchDetails[] }>(
    `/matches/active?wallet=${encodeURIComponent(wallet)}`,
  );
  return response.matches;
};

export const useActiveMatches = (wallet?: string | null) =>
  useQuery({
    queryKey: ['matches', 'active', wallet],
    enabled: Boolean(wallet),
    queryFn: () => fetchActiveMatches(wallet as string),
    staleTime: 2_000,
    refetchInterval: 5_000, // Poll every 5s for active match updates
    refetchOnMount: 'always', // Always fetch fresh data on mount
    placeholderData: (previousData) => previousData, // Smooth transitions
  });

export const useMatchHistory = (wallet?: string | null) =>
  useQuery({
    queryKey: ['matches', 'history', wallet ?? null],
    queryFn: () => {
      const search = wallet ? `?wallet=${encodeURIComponent(wallet)}` : '';
      return http.get<{ history: MatchHistoryEntry[] }>(`/matches/history${search}`);
    },
    select: (data) => data.history,
    refetchOnMount: 'always', // Always fetch fresh data on mount
    staleTime: 10_000,
  });

export const fetchVerifySeeds = (matchId: string) =>
  http.get<VerifySeedResponse>(`/fairness/verify?matchId=${matchId}`);

export interface MatchDetails {
  matchId: string;
  roomId: string | null;
  status: MatchStatus;
  creator: string;
  cluster: string;
  createdAt: number;
  expiresAt: number;
  players: string[];
  serverSeedHash: string;
  matchAccount: string;
  escrowAccount: string;
  nonce: number;
  wager: {
    currency: Currency;
    amountLamports: string;
  };
}

export const fetchMatchDetails = async (matchId: string): Promise<MatchDetails> => {
  const response = await http.get<{ match: MatchDetails }>(`/matches/${matchId}`);
  return response.match;
};

export const requestCancelIntent = async (matchId: string, wallet: string) =>
  http.post<{ instructions: SerializedInstruction[] }>(`/matches/${matchId}/cancel`, {
    wallet,
  });

export const confirmCancel = async (matchId: string, wallet: string, signature: string) =>
  http.post<{ matchId: string; status: MatchStatus; message: string }>(
    `/matches/${matchId}/cancel/confirm`,
    { wallet, signature },
  );

export const requestCloseIntent = async (matchId: string, wallet: string) =>
  http.post<{ instructions: SerializedInstruction[] }>(`/matches/${matchId}/close`, {
    wallet,
  });

export const confirmClose = async (matchId: string, wallet: string, signature: string) =>
  http.post<{ matchId: string; signature: string; message: string }>(
    `/matches/${matchId}/close/confirm`,
    { wallet, signature },
  );

export const fetchCloseableMatches = async (wallet: string): Promise<MatchDetails[]> => {
  const response = await http.get<{ matches: MatchDetails[] }>(
    `/matches/closeable?wallet=${encodeURIComponent(wallet)}`,
  );
  return response.matches;
};

export const useCloseableMatches = (wallet?: string | null) =>
  useQuery({
    queryKey: ['matches', 'closeable', wallet],
    enabled: Boolean(wallet),
    queryFn: () => fetchCloseableMatches(wallet as string),
    staleTime: 10_000,
    refetchOnMount: 'always',
  });

// Orphaned escrow recovery types and functions
export interface OrphanedEscrow {
  matchAccount: string;
  escrowAccount: string;
  status: 'settled' | 'cancelled';
  wagerAmount: string;
  createdAt: number;
}

export const fetchOrphanedEscrows = async (
  wallet: string,
  cluster: string,
): Promise<OrphanedEscrow[]> => {
  const response = await http.get<{ orphaned: OrphanedEscrow[] }>(
    `/matches/orphaned?wallet=${encodeURIComponent(wallet)}&cluster=${encodeURIComponent(cluster)}`,
  );
  return response.orphaned;
};

export const useOrphanedEscrows = (wallet?: string | null, cluster?: string) =>
  useQuery({
    queryKey: ['matches', 'orphaned', wallet, cluster],
    enabled: false, // Only fetch on demand via refetch
    queryFn: () => fetchOrphanedEscrows(wallet as string, cluster ?? 'devnet'),
    staleTime: 30_000,
  });

// Build close intent for orphaned escrows (directly from on-chain data)
export const requestOrphanedCloseIntent = async (
  matchAccount: string,
  escrowAccount: string,
  wallet: string,
  cluster: string,
) => {
  // We need a special endpoint that doesn't require the match to be in server memory
  return http.post<{ instructions: SerializedInstruction[] }>('/matches/orphaned/close', {
    matchAccount,
    escrowAccount,
    wallet,
    cluster,
  });
};
